import React, { useEffect, useState, useCallback } from "react";
import api from "../axiosSetup";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import "../css/marketplace-dark.css";
import cfg from "../../config";

// Funktion statt Konstante: auf Modulebene wuerde der Rueckfallwert aus
// config.js eingefroren, bevor AppContext die Serverwerte uebernimmt.
const STATUS_META = () => ({
  aktiv:      { color: "#22c55e", label: "Aktiv" },
  pausiert:   { color: "#60a5fa", label: "Pausiert" },
  abgelaufen: { color: "#9ca3af", label: "Abgelaufen" },
  archiviert: { color: "#8b5cf6", label: "Archiviert" },
  council:    { color: "#f59e0b", label: `Wartet auf ${cfg.adminRoleName}` },
});

function StatusChip({ status, hasPendingCouncil }) {
  const key = hasPendingCouncil && status === "pausiert" ? "council" : status;
  const m = STATUS_META()[key] ?? { color: "#9ca3af", label: status ?? "—" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "4px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700,
      border: `1px solid ${m.color}`, color: m.color,
      background: `${m.color}18`,
    }}>
      {m.label}
    </span>
  );
}

export default function SRVMyListings({ onCreateNew }) {
  const [items, setItems]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [acting, setActing]       = useState(null); // listingId currently actioned
  const [confirmModal, setConfirm] = useState(null); // { listingId, action, title, text }

  const token = localStorage.getItem("authToken");

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/api/swap-marketplace/listings?status=alle", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const all = Array.isArray(res.data) ? res.data : [];
      setItems(all.filter(l => l.isOwnListing));
    } catch (err) {
      toast.error(err?.response?.data?.message || "Fehler beim Laden deiner Angebote.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetch(); }, [fetch]);

  const doAction = async (listingId, action) => {
    setActing(listingId);
    try {
      const res = await api.patch(
        `/api/swap-marketplace/listings/${listingId}/${action}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data?.message || "Aktion erfolgreich.");
      await fetch();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Aktion fehlgeschlagen.");
    } finally {
      setActing(null);
    }
  };

  const ask = (listingId, action, title, text) =>
    setConfirm({ listingId, action, title, text });

  const confirmAction = async () => {
    if (!confirmModal) return;
    const { listingId, action } = confirmModal;
    setConfirm(null);
    await doAction(listingId, action);
  };

  if (loading) return <p className="text-white-50">Lade deine Angebote…</p>;

  return (
    <div>
      {/* Header-Zeile */}
      <div className="d-flex align-items-center justify-content-between mb-3">
        <span className="text-white-50">{items.length} Angebot{items.length !== 1 ? "e" : ""}</span>
        <button
          className="btn btn-sm btn-outline-warning"
          onClick={onCreateNew}
          style={{ borderRadius: 8 }}
        >
          + Neues Angebot
        </button>
      </div>

      {items.length === 0 ? (
        <div style={{
          padding: "24px", borderRadius: 12, textAlign: "center",
          border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.5)",
        }}>
          Du hast noch keine Angebote erstellt.{" "}
          <button
            className="btn btn-link p-0"
            style={{ color: "#facc15", textDecoration: "none" }}
            onClick={onCreateNew}
          >
            Jetzt erstellen
          </button>
        </div>
      ) : (
        <div className="row g-3">
          {items.map((l) => {
            const isActing            = acting === l.listingId;
            const expiresAt           = l.expiresAt ? dayjs(l.expiresAt).format("DD.MM.YYYY HH:mm") : null;
            const createdAt           = l.createdAt ? dayjs(l.createdAt).format("DD.MM.YYYY") : "—";
            const awaitingCouncil     = !!l.hasPendingCouncilOffer;
            const canManage           = ["aktiv", "pausiert"].includes(l.status) && !awaitingCouncil;

            return (
              <div className="col-12" key={l.listingId}>
                <div style={{
                  borderRadius: 14, border: "1px solid rgba(255,255,255,0.12)",
                  background: "rgba(255,255,255,0.04)", padding: "16px 20px",
                }}>
                  {/* Kopf */}
                  <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                      <div className="fw-semibold" style={{ fontSize: 17 }}>{l.assignmentName}</div>
                      <div className="text-white-50" style={{ fontSize: 13 }}>
                        Erstellt: {createdAt}
                        {expiresAt && ` · Läuft ab: ${expiresAt}`}
                      </div>
                    </div>
                    <StatusChip status={l.status} hasPendingCouncil={awaitingCouncil} />
                  </div>

                  {/* Zunftrat-Hinweis */}
                  {awaitingCouncil && (
                    <div className="mt-2" style={{
                      padding: "8px 12px", borderRadius: 10, fontSize: 13,
                      background: "rgba(245,158,11,.12)", border: "1px solid rgba(245,158,11,.35)",
                      color: "#fde68a",
                    }}>
                      Ein Gegenangebot wurde angenommen und wartet auf die Freigabe durch {cfg.adminRoleName}. Bis zur Entscheidung können keine neuen Gegenangebote gestellt werden.
                    </div>
                  )}

                  {/* Grund */}
                  {l.reason && (
                    <div className="mt-2 text-white-50" style={{ fontSize: 14 }}>
                      <strong className="text-white">Grund:</strong> {l.reason}
                    </div>
                  )}

                  {/* Termine */}
                  {Array.isArray(l.dates) && l.dates.length > 0 && (
                    <ul className="mt-2 mb-0 ps-3 text-white-50" style={{ fontSize: 13 }}>
                      {l.dates.map((d, i) => (
                        <li key={i}>
                          {dayjs(d.workDate).format("DD.MM.YYYY")} —{" "}
                          {String(d.timeFrom).slice(0, 5)}–{String(d.timeTo).slice(0, 5)} Uhr
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* Gegenangebote-Zähler */}
                  <div className="mt-2" style={{ fontSize: 13, color: "#94a3b8" }}>
                    Gegenangebote: <strong className="text-white">{l.offersCount ?? 0}</strong>
                  </div>

                  {/* Aktionen */}
                  {canManage && (
                    <div className="mt-3 d-flex flex-wrap gap-2">
                      {l.status === "aktiv" && (
                        <button
                          className="btn btn-sm btn-outline-primary"
                          disabled={isActing}
                          onClick={() => ask(l.listingId, "pause", "Angebot pausieren?",
                            `Das Angebot „${l.assignmentName}" wird pausiert. Es erscheint nicht mehr im Markt, kann aber jederzeit reaktiviert werden.`)}
                          style={{ borderRadius: 8 }}
                        >
                          Pausieren
                        </button>
                      )}
                      {l.status === "pausiert" && (
                        <button
                          className="btn btn-sm btn-outline-success"
                          disabled={isActing}
                          onClick={() => ask(l.listingId, "resume", "Angebot reaktivieren?",
                            `Das Angebot „${l.assignmentName}" wird wieder aktiv und erscheint im Markt.`)}
                          style={{ borderRadius: 8 }}
                        >
                          Reaktivieren
                        </button>
                      )}
                      <button
                        className="btn btn-sm btn-outline-danger"
                        disabled={isActing}
                        onClick={() => ask(l.listingId, "archive", "Angebot zurückziehen?",
                          `Das Angebot „${l.assignmentName}" wird archiviert und verschwindet aus dem Markt. Diese Aktion kann nicht rückgängig gemacht werden.`)}
                        style={{ borderRadius: 8 }}
                      >
                        Zurückziehen
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Bestätigungs-Modal */}
      {confirmModal && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100"
          style={{ background: "rgba(0,0,0,0.65)", zIndex: 99999, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}
          onClick={(e) => { if (e.target === e.currentTarget) setConfirm(null); }}
        >
          <div style={{ background: "#1e293b", border: "1px solid #334155", borderRadius: 12, width: "100%", maxWidth: 460, padding: "24px", color: "#fff" }}>
            <h6 className="fw-bold mb-2">{confirmModal.title}</h6>
            <p style={{ color: "#94a3b8", fontSize: 14, marginBottom: 20, whiteSpace: "pre-wrap" }}>{confirmModal.text}</p>
            <div className="d-flex justify-content-end gap-2">
              <button className="btn btn-sm btn-secondary" onClick={() => setConfirm(null)}>Abbrechen</button>
              <button className="btn btn-sm btn-warning" onClick={confirmAction}>Bestätigen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
